import type { ReplayManifestTrack } from "./replay-journal";

/**
 * Les caméras d'un enregistrement, et quel fichier montrer à quel instant.
 *
 * # Une caméra, plusieurs fichiers
 *
 * Un MP4 n'annonce qu'une définition, et l'enregistreur en ouvre un nouveau
 * dès qu'une caméra en change (`docs/spec/recorder.md`) : un quart de tour, un
 * zoom qui rogne, la montée en couches du SFU à l'arrivée. Un rideau baissé
 * puis relevé republie la piste sous un autre `sid`, donc un autre fichier
 * encore. Une caméra est donc une **suite** de fichiers, qui se suivent dans
 * le temps.
 *
 * Le lecteur a longtemps pris les fichiers pour des caméras : les deux
 * premiers du manifeste faisaient les deux vignettes. Le 2026-09-21, sur un
 * récital à deux caméras, c'étaient les deux premiers fichiers de la même —
 * une seconde en 320×180 qui tournait en boucle, une image seule en 640×360
 * qui chargeait sans fin, et le même `sid` pour les deux, que React prenait
 * pour une seule clé.
 *
 * Une caméra se reconnaît à **qui la publie** : le moteur vidéo n'en publie
 * qu'une par participant, et c'est la clé sous laquelle la séance disait ce
 * qu'elle filmait. Sa vignette est une, et change de fichier en chemin.
 *
 * Module pur : ni React, ni réseau.
 */

/** Une caméra et ses fichiers, dans l'ordre du temps. */
export interface RecordedCamera {
  /** L'identité LiveKit de qui la publiait. */
  readonly participant: string;
  /**
   * L'instant de séance de sa toute première image, éclats compris : c'est là
   * qu'elle a commencé à filmer, même si le premier fichier montré commence un
   * peu après.
   */
  readonly startMs: number;
  readonly tracks: readonly ReplayManifestTrack[];
}

/**
 * Un bout de temps entre deux fichiers d'une même caméra, sous lequel on ne
 * la cache pas, en millisecondes.
 *
 * Deux séries qui se suivent laissent quelques millisecondes entre la dernière
 * image de l'une et la première de l'autre — quinze, mesurées sur le récital
 * du 2026-09-21. La vignette y disparaîtrait l'espace d'une image, et son
 * élément vidéo avec elle. Une seconde : bien au-delà d'une couture, bien en
 * deçà d'un rideau baissé, qui lui dure et doit se voir.
 */
export const SEAM_MS = 1_000;

/**
 * Un fichier plus court que ça ne se montre pas, en millisecondes — sauf s'il
 * est le seul de sa caméra.
 *
 * Le charger prendrait plus de temps qu'il n'en couvre, et la vignette le
 * passerait sous son voile d'attente. C'est ce que laisse la montée en couches
 * du SFU entre la petite couche et la vraie définition : une image seule en
 * 640×360, cinquante millisecondes (récital du 2026-09-21). Écarté, il laisse
 * une couture, où le fichier suivant montre déjà sa première image
 * ({@link segmentAt}).
 */
export const MIN_SEGMENT_MS = 500;

/**
 * Les caméras d'un enregistrement, de la première arrivée à la dernière, et
 * leurs fichiers dans l'ordre du temps — éclats écartés.
 *
 * Une piste sans image — une caméra qui s'est annoncée sans rien envoyer — ne
 * compte pas.
 */
export function recordedCameras(tracks: readonly ReplayManifestTrack[]): RecordedCamera[] {
  const byParticipant = new Map<string, ReplayManifestTrack[]>();

  for (const track of tracks) {
    if (track.kind !== "video" || track.frames <= 0) {
      continue;
    }

    const own = byParticipant.get(track.participant) ?? [];

    own.push(track);
    byParticipant.set(track.participant, own);
  }

  return Array.from(byParticipant, ([participant, own]) => {
    const sorted = [...own].sort((a, b) => a.originMs - b.originMs);

    return { participant, startMs: sorted[0].originMs, tracks: withoutSlivers(sorted) };
  }).sort((a, b) => a.startMs - b.startMs);
}

/**
 * Écarte les fichiers trop courts pour valoir d'être chargés
 * ({@link MIN_SEGMENT_MS}), sans jamais laisser une caméra sans fichier.
 *
 * Et rien d'autre. La montée en couches du SFU a été jetée — ici, et dans
 * l'enregistreur — comme « les mêmes secondes en moins bien ». Ce sont des
 * secondes à elles, les premières de la caméra : le 2026-09-21, sans elles, le
 * replay s'ouvrait sans image alors que la caméra filmait avant qu'on clique
 * sur « Enregistrer ». Deux secondes en petite définition valent mieux que
 * pas d'image.
 */
function withoutSlivers(tracks: readonly ReplayManifestTrack[]): ReplayManifestTrack[] {
  const kept = tracks.filter((track) => track.durationMs >= MIN_SEGMENT_MS);

  return kept.length > 0 ? kept : [...tracks];
}

/**
 * Le fichier à montrer à cet instant de séance, par son rang ; `null` quand la
 * caméra ne filmait pas.
 *
 * Dans un fichier, c'est lui. Dans une couture de moins de {@link SEAM_MS},
 * c'est le suivant : il est déjà là, et montre sa première image le temps
 * d'arriver. Entre `startMs` — la toute première image, quand un éclat de tête
 * a été écarté — et le premier fichier, c'est le premier, pour la même
 * raison : la caméra filmait déjà. Avant, après la dernière image, ou dans un
 * vrai trou — un rideau —, rien.
 */
export function segmentAt(
  segments: readonly { readonly originMs: number; readonly durationMs: number }[],
  sessionMs: number,
  startMs: number = segments[0]?.originMs ?? Number.POSITIVE_INFINITY,
): number | null {
  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index];

    if (sessionMs > segment.originMs + segment.durationMs) {
      continue;
    }

    if (sessionMs >= segment.originMs) {
      return index;
    }

    const previous = segments[index - 1];

    if (previous === undefined) {
      return sessionMs >= startMs ? index : null;
    }

    return segment.originMs - (previous.originMs + previous.durationMs) < SEAM_MS ? index : null;
  }

  return null;
}
